import { useEffect, useMemo, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import {
  ChevronDown,
  ChevronRight,
  Save,
  Trash2,
  ClipboardPaste,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/common";
import { useTran } from "@/hooks";
import { useAuthStore } from "@/features/auth/hooks";
import { Can } from "@/hooks/useCan/Can";
import { cn } from "@/lib/utils";
import { useDirtyRows } from "../hooks/useDirtyRowsContext";
import {
  getUnitTypeMeta,
  isLevelAbove,
} from "@/features/organization/utils/organization.helpers";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import { toApiOrgId } from "@/features/data-entry/utils/orgTree.helpers";
import { useTargetSetupStore } from "../hooks/useTargetSetupStore";
import { deleteOrgTarget, saveOrgTarget } from "../operations/target-setup.ops";
import {
  buildEmptyTargetData,
  buildEmptyMonthlyTargetData,
  isComputedReadOnly,
  isMonthlyMode,
  getMonthlyFillCount,
  MONTHS,
  resolveDisclosureId,
  buildTargetDataForFrequency,
  hasInvalidNegativeZeroTargetValue,
  hasFilledTargetData,
  formatTargetDisplayValue,
} from "../domains/targetSetup";
import { buildDisclosureYearKey } from "../domains/targetSetup";
import { useTargetSetupUIStore } from "../hooks/useTargetSetupStore";
import TargetValueFields from "./TargetValueFields";
import SetSameValueToAllMonthsDialog from "./SetSameValueToAllMonthsDialog";
import { checkUserFromRole } from "@/helpers";

const INDENT_SIZE = 24;

const OrgTreeRow = observer(
  ({
    node,
    disclosure,
    year,
    monthOptions,
    targetFrequency,
    depth = 0,
    hasChildren,
    collapseKey,
    isCollapsed,
    onToggleCollapse,
  }) => {
    const { t } = useTran();
    const { user, userRole } = useAuthStore();
    const permissions = user?.permissions || [];
    const isUserLevel = checkUserFromRole(userRole);
    const { getOrgTargets } = useTargetSetupStore();
    const { getSaveRequestForDisclosure } = useTargetSetupUIStore();
    const { reportDirty } = useDirtyRows();
    const { registerRowSaveHandler, unregisterRowSaveHandler } =
      useTargetSetupUIStore();

    const orgId = node.id || node._id;
    const meta = getUnitTypeMeta(node.unitLevel);
    const Icon = meta.icon;
    const isGroup = Boolean(node.isGroup);
    const readOnly = isComputedReadOnly(disclosure);
    const hasUnit = Boolean(disclosure?.defaultUnit);
    const disclosureId = resolveDisclosureId(disclosure);
    const isMonthly = isMonthlyMode(targetFrequency);
    const isMonthlyTarget = isMonthly && !isGroup;

    const locationLabel =
      node.locationLabel || node.countryLabel || node.country;

    const existingTarget = getOrgTargets(disclosureId, year)?.get(
      String(orgId),
    );
    const existingTargetHasValue = hasFilledTargetData(
      existingTarget?.targetData,
    );
    const yearlyValueIsDerived =
      !isMonthly &&
      (existingTarget?.targetData?.monthlyValuesSource === "monthly" ||
        (Array.isArray(existingTarget?.targetData?.monthlyValues) &&
          existingTarget.targetData.monthlyValues.length > 0 &&
          existingTarget.targetData.monthlyValuesSource !== "yearly"));
    const initialData = useMemo(
      () =>
        buildTargetDataForFrequency({
          targetData:
            existingTarget?.targetData ||
            (isMonthly
              ? buildEmptyMonthlyTargetData()
              : buildEmptyTargetData(disclosure)),
          disclosure,
          targetFrequency,
          monthOptions,
        }),
      [
        disclosure,
        existingTarget?.targetData,
        isMonthly,
        monthOptions,
        targetFrequency,
      ],
    );

    const [values, setValues] = useState(initialData);
    const [saving, setSaving] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [sameValueDialogOpen, setSameValueDialogOpen] = useState(false);
    const showGrid = isMonthlyTarget && !readOnly && !isCollapsed;

    useEffect(() => {
      setValues(initialData);
    }, [initialData]);

    const hasChanges = JSON.stringify(values) !== JSON.stringify(initialData);

    const rowDirtyKey = `${orgId}-${disclosureId}-${year}`;
    useEffect(() => {
      reportDirty(rowDirtyKey, hasChanges);
      return () => reportDirty(rowDirtyKey, false);
    }, [hasChanges, rowDirtyKey, reportDirty]);

    const inputType = disclosure?.inputType || "";
    const unitOptions = disclosure?.unitOptions || [];

    const monthlyFillCount = isMonthlyTarget
      ? getMonthlyFillCount(values.months)
      : 0;
    const hasInvalidNegativeZero = hasInvalidNegativeZeroTargetValue(
      values,
      inputType,
    );

    const handleSave = useCallback(async () => {
      setSaving(true);
      const result = await saveOrgTarget({
        disclosureId,
        organizationId: toApiOrgId(orgId, user?.companyId),
        year,
        targetData: values,
        existingId: existingTarget?._id,
        disclosure,
        targetFrequency,
      });
      if (result?.targetData) {
        setValues(
          buildTargetDataForFrequency({
            targetData: result.targetData,
            disclosure,
            targetFrequency,
            monthOptions,
          }),
        );
      }
      setSaving(false);
      return result ? true : false;
    }, [
      disclosureId,
      orgId,
      year,
      values,
      existingTarget?._id,
      disclosure,
      monthOptions,
      user?.companyId,
      targetFrequency,
    ]);

    const disclosureKey = buildDisclosureYearKey(disclosureId, year);
    useEffect(() => {
      const rowKey = String(orgId);
      registerRowSaveHandler(disclosureKey, rowKey, () => {
        if (isGroup || !hasChanges) return true;
        if (saving) return false;
        return handleSave();
      });
      return () => unregisterRowSaveHandler(disclosureKey, rowKey);
    }, [
      disclosureKey,
      handleSave,
      hasChanges,
      isGroup,
      orgId,
      registerRowSaveHandler,
      saving,
      unregisterRowSaveHandler,
    ]);

    const handleDelete = useCallback(() => {
      if (!existingTarget?._id) return;
      setDeleteConfirmOpen(true);
    }, [existingTarget?._id]);

    const confirmDelete = useCallback(async () => {
      if (!existingTarget?._id) return;
      setDeleteConfirmOpen(false);
      setSaving(true);
      const success = await deleteOrgTarget({
        id: existingTarget._id,
        disclosureId,
        year,
        orgId: String(orgId),
      });
      if (success)
        setValues(
          isMonthly
            ? buildEmptyMonthlyTargetData()
            : buildEmptyTargetData(disclosure),
        );
      setSaving(false);
    }, [disclosure, disclosureId, existingTarget?._id, isMonthly, orgId, year]);

    const handleApplySameValue = useCallback(
      async (sameValue) => {
        const newMonths = Object.fromEntries(
          MONTHS.map(({ key }) => [key, sameValue]),
        );
        const newValues = { ...values, months: newMonths };
        delete newValues.value;
        delete newValues.monthlyValues;

        setSaving(true);
        const result = await saveOrgTarget({
          disclosureId,
          organizationId: toApiOrgId(orgId, user?.companyId),
          year,
          targetData: newValues,
          existingId: existingTarget?._id,
          disclosure,
        });
        if (result?.targetData) {
          setValues(
            buildTargetDataForFrequency({
              targetData: result.targetData,
              disclosure,
              targetFrequency,
              monthOptions,
            }),
          );
        }
        setSaving(false);
        setSameValueDialogOpen(false);
      },
      [
        values,
        disclosureId,
        orgId,
        year,
        existingTarget?._id,
        disclosure,
        monthOptions,
        user?.companyId,
        targetFrequency,
      ],
    );

    const handleToggle = (e) => {
      e.stopPropagation();
      onToggleCollapse?.(collapseKey);
    };

    return (
      <div
        className={cn(
          "border-b border-border",
          showGrid
            ? "flex flex-col gap-2 px-3 py-3"
            : "flex items-center gap-2 px-3 py-2.5",
        )}
        style={{ paddingLeft: depth * INDENT_SIZE + 12 }}
      >
        {/* Header row */}
        <div className="flex w-full items-center gap-2">
          {/* Expand/collapse children */}
          {hasChildren ? (
            <button
              type="button"
              onClick={handleToggle}
              className="flex h-5 w-5 items-center justify-center rounded border border-border bg-background text-muted-foreground hover:bg-muted"
            >
              {isCollapsed ? (
                <ChevronRight className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </button>
          ) : (
            <span className="ml-2 h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
          )}

          {/* Org icon */}
          <span
            className={`flex h-7 w-7 items-center justify-center rounded-full bg-background shadow-sm ${meta.color}`}
          >
            <Icon className="h-3.5 w-3.5" />
          </span>

          {/* Org info */}
          <div className="flex flex-1 flex-col min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground truncate">
                {node.name}
              </span>
              {locationLabel ? (
                <Badge
                  variant="outline"
                  className="border-primary/30 text-[11px] text-primary shrink-0"
                >
                  {locationLabel}
                </Badge>
              ) : null}
            </div>
            <div className="flex flex-col gap-1">
              {node.code && !isLevelAbove(node.unitLevel, 2) ? (
                <div className="flex items-center gap-1.5 flex-nowrap">
                  <span className={`text-xs ${meta.color} shrink-0`}>
                    code: {t(node.code)}
                  </span>
                  <OrgStatusTag
                    status={node.status}
                    inactiveDate={node.inactiveDate}
                    className="text-[10px] shrink-0"
                  />
                </div>
              ) : (
                <OrgStatusTag
                  status={node.status}
                  inactiveDate={node.inactiveDate}
                  className="text-[10px] shrink-0"
                />
              )}
            </div>
          </div>

          {/* Monthly mode: fill badge + action buttons */}
          {isMonthlyTarget ? (
            readOnly || yearlyValueIsDerived ? (
              <span className="shrink-0 text-xs text-muted-foreground">
                {getMonthlyFillCount(initialData?.months)}/12 {t("months")}
              </span>
            ) : (
              <Can
                resource="target_setting"
                action="update"
                userPermissions={permissions}
                enabled={!isUserLevel}
                behavior="hide"
                fallback={
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {monthlyFillCount}/12 {t("months")}
                  </span>
                }
              >
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSameValueDialogOpen(true)}
                    disabled={saving}
                    className="gap-1.5 text-primary border-primary/30 hover:bg-primary/5"
                  >
                    <ClipboardPaste className="h-3.5 w-3.5" />
                    {t("Set same value to all months")}
                  </Button>

                  <span className="flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs text-primary">
                    {monthlyFillCount}/12 {t("months")}
                  </span>

                  <Can
                    resource="target_setting"
                    action="delete"
                    userPermissions={permissions}
                    enabled={!isUserLevel}
                    behavior="hide"
                  >
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={handleDelete}
                      disabled={
                        saving ||
                        !existingTarget?._id ||
                        !existingTargetHasValue
                      }
                      className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      title={t("Delete")}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </Can>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={handleSave}
                    disabled={saving || !hasChanges || hasInvalidNegativeZero}
                    className="h-8 w-8 text-muted-foreground hover:text-primary"
                    title={t("Save")}
                  >
                    <Save className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </Can>
            )
          ) : null}

          {/* Yearly mode: inline target fields */}
          {!isGroup && !isMonthlyTarget ? (
            readOnly ? (
              <div className="flex items-center gap-2 shrink-0">
                <span className="w-40 text-center text-sm text-muted-foreground">
                  {formatTargetDisplayValue(initialData?.value, inputType)}
                </span>
                {hasUnit ? (
                  <span className="w-40 text-center text-sm text-muted-foreground">
                    {initialData?.unit || "—"}
                  </span>
                ) : null}
                <span className="w-20" />
              </div>
            ) : (
              <Can
                resource="target_setting"
                action="update"
                userPermissions={permissions}
                enabled={!isUserLevel}
                behavior="hide"
                fallback={
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="w-40 text-center text-sm text-muted-foreground">
                      {formatTargetDisplayValue(values.value, inputType)}
                    </span>
                    {hasUnit ? (
                      <span className="w-40 text-center text-sm text-muted-foreground">
                        {disclosure.defaultUnit}
                      </span>
                    ) : null}
                    <span className="w-20" />
                  </div>
                }
              >
                <div className="flex items-center gap-2 shrink-0">
                  <TargetValueFields
                    disclosure={disclosure}
                    values={values}
                    onChange={setValues}
                    disabled={saving}
                    inputType={inputType}
                    unitOptions={unitOptions}
                    isMonthly={false}
                  />
                  <div className="flex w-20 items-center justify-end gap-2">
                    <Can
                      resource="target_setting"
                      action="delete"
                      userPermissions={permissions}
                      enabled={!isUserLevel}
                      behavior="hide"
                    >
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={handleDelete}
                        disabled={
                          saving ||
                          !existingTarget?._id ||
                          !existingTargetHasValue
                        }
                        className="h-9 w-9 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        title={t("Delete")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </Can>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={handleSave}
                      disabled={saving || !hasChanges || hasInvalidNegativeZero}
                      className="h-9 w-9 text-muted-foreground hover:text-primary"
                      title={t("Save")}
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Can>
            )
          ) : null}
        </div>

        {/* Monthly grid */}
        {showGrid ? (
          <Can
            resource="target_setting"
            action="update"
            userPermissions={permissions}
            enabled={!isUserLevel}
            behavior="hide"
          >
            <div className="ml-9">
              <TargetValueFields
                disclosure={disclosure}
                values={values}
                onChange={setValues}
                disabled={saving}
                inputType={inputType}
                unitOptions={unitOptions}
                monthOptions={monthOptions}
                isMonthly
              />
            </div>
          </Can>
        ) : null}

        <ConfirmDialog
          open={deleteConfirmOpen}
          onOpenChange={setDeleteConfirmOpen}
          title={t("Delete target")}
          description={t(
            "This action cannot be undone. The target data will be permanently deleted and cannot be recovered.",
          )}
          confirmLabel={t("Delete")}
          cancelLabel={t("Cancel")}
          loading={saving}
          tone="danger"
          onConfirm={confirmDelete}
        />

        {isMonthlyTarget ? (
          <SetSameValueToAllMonthsDialog
            open={sameValueDialogOpen}
            onOpenChange={setSameValueDialogOpen}
            onApply={handleApplySameValue}
            orgName={node.name}
            disclosureCode={disclosure?.code}
            disclosureTitle={disclosure?.title}
            unit={disclosure?.defaultUnit}
            loading={saving}
          />
        ) : null}
      </div>
    );
  },
);

export default OrgTreeRow;
